import type { StrategyRadarListingKind } from './types'

export type RadarTone = 'positive' | 'warning' | 'negative' | 'neutral'

export type RadarBar = {
  openTime: number
  closeTime: number
  open: number
  high: number
  low: number
  close: number
  volume: number
  quoteVolume: number
}

export type SwingSignal = {
  state: 'buy-watch' | 'hold' | 'wait' | 'exit-watch' | 'unavailable'
  label: string
  tone: RadarTone
  outlook: '偏强' | '震荡' | '防守' | '待核验'
  price: number | null
  ema20: number | null
  ema50: number | null
  rsi14: number | null
  rangeLow: number | null
  rangeHigh: number | null
  entryRule: string
  exitRule: string
  reasons: string[]
  momentum: number[]
  dataTime: number | null
  latestPrice: number | null
  latestPriceAsOf: number | null
  trendForecast: 'bullish' | 'range' | 'defensive' | 'unavailable'
  forecastSummary: string
  forecastHorizon: 'NEXT_1_TO_3_CLOSED_4H'
  forecastConfidence: 'high' | 'medium' | 'low' | 'unavailable'
  positionTier: 'none' | 'watch' | 'small' | 'reduce'
  positionGuidance: string
}

export type ListingEntryConfirmation = {
  state: 'confirmed' | 'waiting' | 'blocked' | 'unavailable'
  label: string
  tone: RadarTone
  summary: string
  dataTime: number | null
}

export type BStockUniverseItem = {
  symbol: string
  token: string
  fullName: string
  company: string
  underlying: string | null
  theme: string | null
  volume: number
  registryStatus: 'registered' | 'official-discovered'
}

export type BStockDiscovery = {
  items: BStockUniverseItem[]
  discoveredCount: number
  selectedCount: number
  limit: number
  truncated: boolean
}

export type BtcRangeZone = {
  id: 'below' | 'accumulate' | 'observe' | 'reduce' | 'breakout'
  label: string
  tone: RadarTone
  action: string
  positionPct: number
}

export type ListingPhase = {
  state: 'scheduled' | 'preopen' | 'opening' | 'entry-window' | 'confirm' | 'expired' | 'unknown'
  label: string
  tone: RadarTone
  action: string
  countdownMs: number | null
}

export type ListingReminder = {
  id: 'preopen' | 'opening' | 'entry-window'
  label: string
}

export type ParsedAnnouncement = {
  code: string
  title: string
  releaseAt: number
  articleUrl: string
  tradingAt: number | null
  symbols: string[]
}

export const RADAR_METHODOLOGY_VERSION = 'ta-core-1.1.0+spot-radar-v1'

export const BSTOCK_REGISTRY = [
  { symbol: 'NVDABUSDT', token: 'NVDAB', underlying: 'NVDA', company: 'NVIDIA', theme: 'AI 算力' },
  { symbol: 'TSLABUSDT', token: 'TSLAB', underlying: 'TSLA', company: 'Tesla', theme: '智能汽车' },
  { symbol: 'AAPLBUSDT', token: 'AAPLB', underlying: 'AAPL', company: 'Apple', theme: '消费科技' },
  { symbol: 'AMZNBUSDT', token: 'AMZNB', underlying: 'AMZN', company: 'Amazon', theme: '云与电商' }
] as const

export const STOCK_MAPPINGS = BSTOCK_REGISTRY

export const MAINSTREAM_MARKETS = [
  { symbol: 'BTCUSDT', asset: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETHUSDT', asset: 'ETH', name: 'Ethereum' },
  { symbol: 'BNBUSDT', asset: 'BNB', name: 'BNB' }
] as const

const BSTOCK_PRIORITY = ['NVDABUSDT', 'TSLABUSDT'] as const
const BSTOCK_NAME_SUFFIX = ' (bStocks)'

export function discoverBStockUniverse(payload: unknown, limit = 8): BStockDiscovery {
  const boundedLimit = Number.isInteger(limit) && limit > 0 ? Math.min(limit, 20) : 8
  const rows = payload && typeof payload === 'object'
    ? (payload as Record<string, unknown>).data
    : null
  if (!Array.isArray(rows)) throw new Error('Binance bStocks 官方列表响应缺少 data')

  const registry = new Map<string, (typeof BSTOCK_REGISTRY)[number]>(
    BSTOCK_REGISTRY.map(item => [item.symbol, item])
  )
  const discovered = new Map<string, BStockUniverseItem>()
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const record = row as Record<string, unknown>
    const symbol = typeof record.symbol === 'string' ? record.symbol : ''
    const token = typeof record.baseAsset === 'string' ? record.baseAsset : ''
    const quoteAsset = typeof record.quoteAsset === 'string' ? record.quoteAsset : ''
    const fullName = typeof record.fullName === 'string' ? record.fullName.trim() : ''
    const tags = Array.isArray(record.tags) ? record.tags : []
    const volume = typeof record.volume === 'number' ? record.volume : Number(record.volume)
    const listingTime = typeof record.listingTime === 'number' ? record.listingTime : Number(record.listingTime)
    if (
      quoteAsset !== 'USDT'
      || !/^[A-Z0-9]{2,20}USDT$/.test(symbol)
      || !/^[A-Z0-9]{2,14}$/.test(token)
      || symbol !== `${token}${quoteAsset}`
      || !tags.includes('bStocks')
      || !fullName
      || !Number.isFinite(listingTime)
      || listingTime <= 0
      || !Number.isFinite(volume)
      || volume < 0
    ) continue
    const company = fullName.endsWith(BSTOCK_NAME_SUFFIX)
      ? fullName.slice(0, -BSTOCK_NAME_SUFFIX.length).trim()
      : fullName
    if (!company) continue
    const registered = registry.get(symbol)
    discovered.set(symbol, {
      symbol,
      token,
      fullName,
      company,
      underlying: registered?.underlying ?? null,
      theme: registered?.theme ?? null,
      volume,
      registryStatus: registered ? 'registered' : 'official-discovered'
    })
  }

  const priority = BSTOCK_PRIORITY.flatMap(symbol => {
    const item = discovered.get(symbol)
    if (!item) return []
    discovered.delete(symbol)
    return [item]
  })
  const ranked = [...discovered.values()].sort((left, right) => (
    right.volume - left.volume || left.symbol.localeCompare(right.symbol)
  ))
  const all = [...priority, ...ranked]
  const items = all.slice(0, boundedLimit)
  return {
    items,
    discoveredCount: all.length,
    selectedCount: items.length,
    limit: boundedLimit,
    truncated: all.length > items.length
  }
}

export function parseBinanceKlines(payload: unknown, now = Date.now()): RadarBar[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap(item => {
    if (!Array.isArray(item) || item.length < 8) return []
    const bar: RadarBar = {
      openTime: Number(item[0]),
      open: Number(item[1]),
      high: Number(item[2]),
      low: Number(item[3]),
      close: Number(item[4]),
      volume: Number(item[5]),
      closeTime: Number(item[6]),
      quoteVolume: Number(item[7])
    }
    if (
      !Number.isFinite(bar.openTime)
      || !Number.isFinite(bar.closeTime)
      || bar.closeTime >= now
      || ![bar.open, bar.high, bar.low, bar.close, bar.volume, bar.quoteVolume].every(Number.isFinite)
      || bar.open <= 0
      || bar.high < Math.max(bar.open, bar.close)
      || bar.low > Math.min(bar.open, bar.close)
      || bar.low <= 0
      || bar.volume < 0
      || bar.quoteVolume < 0
    ) return []
    return [bar]
  }).sort((left, right) => left.openTime - right.openTime)
}

export function analyzeSwing(bars: RadarBar[]): SwingSignal {
  if (bars.length < 55) return unavailableSignal()
  const closes = bars.map(item => item.close)
  const last = closes[closes.length - 1]
  const ema20Value = ema(closes, 20)
  const ema50Value = ema(closes, 50)
  const rsiValue = rsi(closes, 14)
  const recent = bars.slice(-20)
  const rangeLow = Math.min(...recent.map(item => item.low))
  const rangeHigh = Math.max(...recent.map(item => item.high))
  const trendUp = last > ema20Value && ema20Value > ema50Value
  const trendBroken = last < ema20Value
  const pullbackLow = Math.min(...bars.slice(-3).map(item => item.low))
  const confirmedEma20Pullback = (
    last >= ema20Value
    && last <= ema20Value * 1.025
    && pullbackLow >= ema20Value * 0.985
    && pullbackLow <= ema20Value * 1.01
  )
  const healthyRsi = rsiValue >= 44 && rsiValue <= 67
  const overheated = rsiValue >= 72
  const reasons = [
    last > ema20Value ? '价格在 EMA20 上方' : '价格在 EMA20 下方',
    ema20Value > ema50Value ? 'EMA20 高于 EMA50' : 'EMA20 未高于 EMA50',
    `RSI14 ${rsiValue.toFixed(1)}`
  ]

  let state: SwingSignal['state'] = 'wait'
  let label = '等待确认'
  let tone: RadarTone = 'neutral'
  let outlook: SwingSignal['outlook'] = '震荡'
  let entryRule = '4H 收盘重回 EMA20 上方，且 RSI14 回到 44–67'
  let exitRule = '4H 收盘跌破 EMA20，或 RSI14 超过 72 后转弱'

  if (trendBroken || rsiValue < 34) {
    state = 'exit-watch'
    label = '退出观察'
    tone = 'negative'
    outlook = '防守'
    entryRule = '暂不新开仓；等待重新站回 EMA20，弱势时再确认 EMA50'
    exitRule = '4H 已跌破 EMA20 或 RSI14 失速，优先检查退出；EMA50 是二次防线'
  } else if (overheated) {
    state = 'exit-watch'
    label = '高位降风险'
    tone = 'warning'
    outlook = '偏强'
    entryRule = '不追高；等 RSI 回落与 EMA20 回踩'
    exitRule = 'RSI 高位回落或 4H 跌破 EMA20 时检查减仓'
  } else if (trendUp && confirmedEma20Pullback && healthyRsi) {
    state = 'buy-watch'
    label = '买入观察'
    tone = 'positive'
    outlook = '偏强'
    entryRule = '4H 回踩 EMA20 不破并收回其上，分批小额确认'
  } else if (trendUp) {
    state = 'hold'
    label = '趋势延续 / 等回踩'
    tone = 'positive'
    outlook = '偏强'
    entryRule = '趋势尚在，但不在理想回踩区；等 EMA20 附近'
  }

  const guidance = swingGuidance(state, {
    trendBroken,
    lowRsi: rsiValue < 34,
    trendUp,
    confirmedPullback: confirmedEma20Pullback,
    healthyRsi,
    overheated
  })

  return {
    state,
    label,
    tone,
    outlook,
    price: last,
    ema20: ema20Value,
    ema50: ema50Value,
    rsi14: rsiValue,
    rangeLow,
    rangeHigh,
    entryRule,
    exitRule,
    reasons,
    momentum: normalizeMomentum(closes.slice(-24)),
    dataTime: bars[bars.length - 1].closeTime,
    latestPrice: null,
    latestPriceAsOf: null,
    ...guidance
  }
}

export function guardSwingSignal(
  signal: SwingSignal,
  tradingStatus: string | null,
  isSpotTradingAllowed: boolean | null,
  now = Date.now()
): SwingSignal {
  if (tradingStatus !== 'TRADING' || isSpotTradingAllowed !== true) {
    return blockedSignal(
      signal,
      tradingStatus !== 'TRADING'
        ? (tradingStatus ? `现货状态 ${tradingStatus}` : '交易状态待核验')
        : '现货交易权限待核验',
      '币安现货状态必须为 TRADING 且 isSpotTradingAllowed=true，否则本轮不生成买卖观察。'
    )
  }
  if (signal.state === 'unavailable') return signal
  const maximumAgeMs = 8 * 60 * 60_000 + 5 * 60_000
  if (signal.dataTime == null || signal.dataTime > now || now - signal.dataTime > maximumAgeMs) {
    return blockedSignal(
      signal,
      '4H 数据已过期',
      '最后一根已闭合 4H K 线超过两根周期未更新，本轮不生成买卖观察。'
    )
  }
  return signal
}

function swingGuidance(state: SwingSignal['state'], context: {
  trendBroken: boolean
  lowRsi: boolean
  trendUp: boolean
  confirmedPullback: boolean
  healthyRsi: boolean
  overheated: boolean
}): Pick<
  SwingSignal,
  'trendForecast' | 'forecastSummary' | 'forecastHorizon' | 'forecastConfidence' | 'positionTier' | 'positionGuidance'
> {
  const forecastHorizon = 'NEXT_1_TO_3_CLOSED_4H' as const
  let trendForecast: SwingSignal['trendForecast']
  let forecastConfidence: SwingSignal['forecastConfidence']
  let forecastSummary: string
  if (context.trendBroken || context.lowRsi) {
    trendForecast = 'defensive'
    forecastConfidence = context.trendBroken && context.lowRsi ? 'high' : 'medium'
    forecastSummary = '价格位于 EMA20 下方或 RSI14 低于 34；未来 1–3 根已闭合 4H K 线按防守情景观察，不是价格预测。'
  } else if (context.trendUp) {
    trendForecast = 'bullish'
    forecastConfidence = context.confirmedPullback && context.healthyRsi && !context.overheated
      ? 'high'
      : 'medium'
    forecastSummary = '价格、EMA20 与 EMA50 保持多头排列；未来 1–3 根已闭合 4H K 线按偏强延续情景观察，不是价格预测。'
  } else {
    trendForecast = 'range'
    forecastConfidence = 'low'
    forecastSummary = '均线未形成完整多头排列；未来 1–3 根已闭合 4H K 线按震荡情景观察，不是价格预测。'
  }

  const positionTier: SwingSignal['positionTier'] = state === 'buy-watch'
    ? 'small'
    : state === 'exit-watch'
      ? 'reduce'
      : 'watch'
  const positionGuidance = state === 'buy-watch'
    ? '仅小额分批人工确认；不提供自动下单或数字仓位。'
    : state === 'exit-watch'
      ? '停止新开仓，人工复核减仓或退出；不设固定止盈。'
      : '保持观察，等待已闭合 4H 规则确认；不追价。'
  return {
    trendForecast,
    forecastSummary,
    forecastHorizon,
    forecastConfidence,
    positionTier,
    positionGuidance
  }
}

function unavailableGuidance(): Pick<
  SwingSignal,
  'trendForecast' | 'forecastSummary' | 'forecastHorizon' | 'forecastConfidence' | 'positionTier' | 'positionGuidance'
> {
  return {
    trendForecast: 'unavailable',
    forecastSummary: '数据门禁未通过，不预测未来 1–3 根已闭合 4H。',
    forecastHorizon: 'NEXT_1_TO_3_CLOSED_4H',
    forecastConfidence: 'unavailable',
    positionTier: 'none',
    positionGuidance: '不生成仓位动作，等待数据恢复并重新校验。'
  }
}

export function btcRangeZone(price: number | null): BtcRangeZone {
  if (price == null || !Number.isFinite(price) || price <= 0) {
    return { id: 'below', label: '价格待核验', tone: 'neutral', action: '没有可用现货价格，不生成区间动作。', positionPct: 0 }
  }
  const positionPct = Math.max(0, Math.min(100, (price - 60_000) / 10_000 * 100))
  if (price < 60_000) {
    return { id: 'below', label: '区间失效', tone: 'negative', action: '暂停 6–7 万区间策略，等价格重新收回 60,000 上方。', positionPct }
  }
  if (price < 62_000) {
    return { id: 'accumulate', label: '低位观察', tone: 'positive', action: '只在 4H 止跌且 RSI 回升时分批小额；不猜底。', positionPct }
  }
  if (price < 68_000) {
    return { id: 'observe', label: '区间中段', tone: 'neutral', action: '不追价；回踩 EMA20 确认后才考虑小额波段。', positionPct }
  }
  if (price <= 70_000) {
    return { id: 'reduce', label: '高位管理', tone: 'warning', action: '进入 68,000–70,000 上沿，不新追高；按趋势转弱检查减仓。', positionPct }
  }
  return { id: 'breakout', label: '突破待确认', tone: 'warning', action: '区间上破；等 4H 收盘确认与回踩，不把瞬时上破当成已稳固。', positionPct }
}

export function listingPhase(tradingAt: number | null, now = Date.now()): ListingPhase {
  if (tradingAt == null || !Number.isFinite(tradingAt)) {
    return { state: 'unknown', label: '交易时间待核对', tone: 'neutral', action: '已读取官方公告，但未从正文提取到明确 UTC 开盘时间。', countdownMs: null }
  }
  const countdownMs = tradingAt - now
  const minutesToOpen = countdownMs / 60_000
  const minutesSinceOpen = -minutesToOpen
  if (minutesToOpen > 30) {
    return { state: 'scheduled', label: '已定时', tone: 'positive', action: '上线前 15 分钟进入观察，提前打开 1m K 线，不预挂追价单。', countdownMs }
  }
  if (minutesToOpen > 0) {
    return { state: 'preopen', label: '临近开盘', tone: 'warning', action: '只记录开盘价和首轮高点；开盘前不追价。', countdownMs }
  }
  if (minutesSinceOpen < 3) {
    return { state: 'opening', label: '首根禁追', tone: 'negative', action: '等待至少 3 分钟，让首轮拉升与流动性波动先暴露。', countdownMs }
  }
  if (minutesSinceOpen <= 15) {
    return { state: 'entry-window', label: '首轮候选窗口', tone: 'positive', action: '这是候选时段，不是买入信号；打开 1m 图手动核对开盘价、首次回踩与流动性。', countdownMs }
  }
  if (minutesSinceOpen <= 45) {
    return { state: 'confirm', label: '二次确认', tone: 'warning', action: '首轮窗口已过；只等二次突破确认，不追中间价。', countdownMs }
  }
  return { state: 'expired', label: '超短窗口已过', tone: 'neutral', action: '不再按上新超短策略处理；如需参与，转入普通技术图表重新评估。', countdownMs }
}

export function listingReminder(tradingAt: number | null, now = Date.now()): ListingReminder | null {
  if (tradingAt == null || !Number.isFinite(tradingAt)) return null
  if (now >= tradingAt - 15 * 60_000 && now < tradingAt) {
    return { id: 'preopen', label: '进入 T−15 上线准备窗' }
  }
  if (now >= tradingAt && now < tradingAt + 3 * 60_000) {
    return { id: 'opening', label: '已开盘，首 3 分钟禁追' }
  }
  if (now >= tradingAt + 3 * 60_000 && now <= tradingAt + 15 * 60_000) {
    return { id: 'entry-window', label: '进入 T+3–15 候选观察窗' }
  }
  return null
}

export function listingEntryConfirmation(
  bars: RadarBar[],
  tradingAt: number | null,
  tradingStatus: string | null,
  isSpotTradingAllowed: boolean | null,
  now = Date.now()
): ListingEntryConfirmation {
  if (tradingAt == null || !Number.isFinite(tradingAt)) return {
    state: 'unavailable',
    label: '开盘时间待核验',
    tone: 'neutral',
    summary: '未确认官方开盘时间，不判定 1m 入场确认。',
    dataTime: null
  }
  const phase = listingPhase(tradingAt, now)
  if (['scheduled', 'preopen', 'opening'].includes(phase.state)) return {
    state: 'waiting',
    label: '等待 T+3 确认窗',
    tone: 'neutral',
    summary: '开盘前与首 3 分钟不请求入场确认，不追首轮拉升。',
    dataTime: null
  }
  if (phase.state !== 'entry-window') return {
    state: 'blocked',
    label: '候选窗已关闭',
    tone: 'warning',
    summary: '只在 T+3–15 分钟判定首轮 1m 回撤恢复；当前不生成入场候选。',
    dataTime: null
  }
  if (tradingStatus !== 'TRADING' || isSpotTradingAllowed !== true) return {
    state: 'blocked',
    label: '交易状态未通过',
    tone: 'warning',
    summary: tradingStatus !== 'TRADING'
      ? (tradingStatus
          ? `币安现货状态为 ${tradingStatus}，不生成 1m 入场候选。`
          : '未核验币安现货 TRADING 状态，不生成 1m 入场候选。')
      : '币安未确认 isSpotTradingAllowed=true，不生成 1m 入场候选。',
    dataTime: null
  }

  const opened = bars
    .filter(bar => bar.closeTime >= tradingAt && bar.closeTime <= now)
    .sort((left, right) => left.openTime - right.openTime)
  const latest = opened[opened.length - 1]
  if (opened.length < 5 || !latest) return unavailableEntryConfirmation('至少需要 5 根开盘后已闭合 1m K 线。', latest?.closeTime ?? null)
  if (now - latest.closeTime > 2 * 60_000 + 15_000) {
    return unavailableEntryConfirmation('最新已闭合 1m K 线超过 2 分钟未更新。', latest.closeTime)
  }
  if (opened.some((bar, index) => index > 0 && bar.openTime - opened[index - 1].openTime > 90_000)) {
    return unavailableEntryConfirmation('开盘后 1m K 线存在缺口。', latest.closeTime)
  }

  const peakCandidates = opened.slice(0, -2)
  let peakIndex = 0
  for (let index = 1; index < peakCandidates.length; index += 1) {
    if (peakCandidates[index].high > peakCandidates[peakIndex].high) peakIndex = index
  }
  const troughCandidates = opened.slice(peakIndex + 1, -1)
  if (!troughCandidates.length) return waitingEntryConfirmation('首轮高点后尚无可确认回撤。', latest.closeTime)
  let troughOffset = 0
  for (let index = 1; index < troughCandidates.length; index += 1) {
    if (troughCandidates[index].low < troughCandidates[troughOffset].low) troughOffset = index
  }
  const troughIndex = peakIndex + 1 + troughOffset
  const peak = opened[peakIndex].high
  const trough = opened[troughIndex].low
  const pullbackDepth = (peak - trough) / peak
  if (pullbackDepth < 0.02) return waitingEntryConfirmation('尚未出现至少 2% 的首轮回撤，不追价。', latest.closeTime)

  const previous = opened[opened.length - 2]
  const recoveryRatio = (latest.close - trough) / Math.max(peak - trough, peak * 0.0001)
  if (recoveryRatio < 0.4 || latest.close <= previous.close) {
    return waitingEntryConfirmation('回撤后尚未收回至少 40% 跌幅且未确认向上。', latest.closeTime)
  }
  const baselineVolumes = opened.slice(1, -1).map(bar => bar.quoteVolume).sort((left, right) => left - right)
  const medianVolume = baselineVolumes[Math.floor(baselineVolumes.length / 2)]
  if (!Number.isFinite(medianVolume) || medianVolume <= 0 || latest.quoteVolume < medianVolume * 0.8) {
    return waitingEntryConfirmation('价格已恢复，但最新成交额未达近期中位数的 80%。', latest.closeTime)
  }
  return {
    state: 'confirmed',
    label: '1m 候选确认',
    tone: 'positive',
    summary: '首轮高点后回撤≥2%，随后收回≥40% 跌幅且成交额保持活跃；仅为人工复核候选。',
    dataTime: latest.closeTime
  }
}

export function classifyListingAnnouncement(title: string): StrategyRadarListingKind | null {
  const normalized = title.toLocaleLowerCase()
  if (/futures|perpetual|margin|collateral|convert|bstocks|tokenized securit/.test(normalized)) return null
  if (isListingScheduleUpdate(title)) return 'other'
  if (/trading pair|trading bots/.test(normalized)) return null
  if (/\blaunchpool\b/.test(normalized)) {
    return /\bintroducing\b|\bnew project\b|\bfarm\b.+\blaunchpool\b/.test(normalized)
      ? 'launchpool'
      : null
  }
  if (/\blaunchpad\b/.test(normalized)) {
    return /\bintroducing\b|\btoken sale\b|\bsubscription\b.+\b(?:open|complete)|\bwill open trading\b/.test(normalized)
      ? 'launchpad'
      : null
  }
  if (/\bmega\s*drop\b/.test(normalized)) {
    return /\bintroducing\b|\bon binance mega\s*drop\b|\bmega\s*drop is now open\b/.test(normalized)
      ? 'megadrop'
      : null
  }
  if (/\bhodler airdrops?\b/.test(normalized)) {
    return /\bintroducing\b.+\bon binance hodler airdrops?\b/.test(normalized)
      ? 'airdrop'
      : null
  }
  if (/\bbinance alpha\b/.test(normalized)) {
    return /\bwill (?:add|list|be available)\b|\bopen for trading\b|\bintroducing\b/.test(normalized) ? 'alpha' : null
  }
  if (/\bwill list\b|\bnew (?:coin )?listing\b/.test(normalized)) return 'spot_listing'
  return null
}

export function isListingScheduleUpdate(title: string) {
  const normalized = title.toLocaleLowerCase()
  return (
    /\blisting\b/.test(normalized)
    && /\bpostpon(?:e|ed)|\bdelay(?:ed)?|\breschedul(?:e|ed)|\bcancel(?:led|ed|lation)|\btime (?:change|update)/.test(normalized)
  )
}

export function extractScheduleUpdateTradingAt(text: string): number | null {
  if (/\bcancel(?:led|ed|lation)\b|\bwill not (?:list|open trading)\b/i.test(text)) return null
  const timestampPattern = /\b(20\d{2}-\d{2}-\d{2})\s+(\d{2}:\d{2})(?::\d{2})?\s*\(UTC\)/gi
  const matches = [...text.matchAll(timestampPattern)]
  const match = matches.length ? matches[matches.length - 1] : undefined
  if (!match) return null
  const timestamp = Date.parse(`${match[1]}T${match[2]}:00Z`)
  return Number.isFinite(timestamp) ? timestamp : null
}

export function isListingScheduleCancellation(title: string, text: string) {
  return /\bcancel(?:led|ed|lation)\b|\bwill not (?:list|open trading)\b/i.test(`${title} ${text}`)
}

export function isSpotListingAnnouncement(title: string) {
  return classifyListingAnnouncement(title) === 'spot_listing'
}

export function articlePlainText(body: unknown): string {
  let root = body
  if (typeof body === 'string') {
    try {
      root = JSON.parse(body)
    } catch {
      return body.replace(/<[^>]+>/g, ' ')
    }
  }
  const fragments: string[] = []
  const visit = (value: unknown) => {
    if (typeof value === 'string') {
      fragments.push(value)
      return
    }
    if (Array.isArray(value)) {
      value.forEach(visit)
      return
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>
      if (record.node === 'text' && typeof record.text === 'string') {
        fragments.push(record.text)
        return
      }
      if ('child' in record) {
        visit(record.child)
        return
      }
      Object.entries(record).forEach(([key, child]) => {
        if (!['node', 'tag', 'attr'].includes(key)) visit(child)
      })
    }
  }
  visit(root)
  return fragments.join(' ').replace(/\s+/g, ' ').trim()
}

export function extractTradingAt(text: string): number | null {
  const timestampPattern = /\b(20\d{2}-\d{2}-\d{2})\s+(\d{2}:\d{2})(?::\d{2})?\s*\(UTC\)/gi
  const matches = [...text.matchAll(timestampPattern)]
  if (!matches.length) return null
  const tradingMatch = matches.find(match => {
    const before = text.slice(Math.max(0, (match.index ?? 0) - 260), match.index ?? 0)
    return /(?:open|start|begin|commence|launch)[^.]{0,180}trading|trading[^.]{0,180}(?:open|start|begin|commence|launch)/i.test(before)
  })
  const match = tradingMatch ?? (matches.length === 1 ? matches[0] : null)
  if (!match) return null
  const timestamp = Date.parse(`${match[1]}T${match[2]}:00Z`)
  return Number.isFinite(timestamp) ? timestamp : null
}

export function extractSpotSymbols(text: string, title = ''): string[] {
  const symbols = new Set<string>()
  const pairPattern = /\b([A-Z0-9]{2,14})\s*\/\s*(USDT|USDC|FDUSD)\b/g
  let match: RegExpExecArray | null
  while ((match = pairPattern.exec(text.toUpperCase())) !== null) symbols.add(`${match[1]}${match[2]}`)
  if (!symbols.size) {
    const titlePattern = /\(([A-Z0-9]{2,12})\)/g
    while ((match = titlePattern.exec(title.toUpperCase())) !== null) symbols.add(`${match[1]}USDT`)
  }
  return [...symbols].slice(0, 20)
}

export function formatCountdown(milliseconds: number | null) {
  if (milliseconds == null || !Number.isFinite(milliseconds)) return '待核对'
  const past = milliseconds < 0
  const totalSeconds = Math.max(0, Math.floor(Math.abs(milliseconds) / 1000))
  const days = Math.floor(totalSeconds / 86_400)
  const hours = Math.floor(totalSeconds % 86_400 / 3_600)
  const minutes = Math.floor(totalSeconds % 3_600 / 60)
  const seconds = totalSeconds % 60
  const clock = days > 0
    ? `${days}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return past ? `已开盘 ${clock}` : `T− ${clock}`
}

function ema(values: number[], period: number) {
  if (values.length < period) return Number.NaN
  const multiplier = 2 / (period + 1)
  let result = values.slice(0, period).reduce((sum, value) => sum + value, 0) / period
  for (let index = period; index < values.length; index += 1) {
    result = values[index] * multiplier + result * (1 - multiplier)
  }
  return result
}

function rsi(values: number[], period: number) {
  const changes = values.slice(1).map((value, index) => value - values[index])
  const seed = changes.slice(0, period)
  let averageGain = seed.reduce((sum, value) => sum + Math.max(value, 0), 0) / period
  let averageLoss = seed.reduce((sum, value) => sum + Math.max(-value, 0), 0) / period
  for (const change of changes.slice(period)) {
    averageGain = (averageGain * (period - 1) + Math.max(change, 0)) / period
    averageLoss = (averageLoss * (period - 1) + Math.max(-change, 0)) / period
  }
  if (averageLoss === 0) return 100
  return 100 - 100 / (1 + averageGain / averageLoss)
}

function normalizeMomentum(values: number[]) {
  if (!values.length) return []
  const minimum = Math.min(...values)
  const maximum = Math.max(...values)
  const range = Math.max(maximum - minimum, maximum * 0.0001, 1e-9)
  return values.map(value => Math.max(8, Math.min(100, (value - minimum) / range * 82 + 9)))
}

function waitingEntryConfirmation(summary: string, dataTime: number): ListingEntryConfirmation {
  return { state: 'waiting', label: '等待 1m 确认', tone: 'neutral', summary, dataTime }
}

function unavailableEntryConfirmation(summary: string, dataTime: number | null): ListingEntryConfirmation {
  return { state: 'unavailable', label: '1m 数据待核验', tone: 'neutral', summary, dataTime }
}

function unavailableSignal(): SwingSignal {
  return {
    state: 'unavailable',
    label: '数据待核验',
    tone: 'neutral',
    outlook: '待核验',
    price: null,
    ema20: null,
    ema50: null,
    rsi14: null,
    rangeLow: null,
    rangeHigh: null,
    entryRule: '至少需要 55 根已闭合 4H K 线才生成观察信号',
    exitRule: '数据不足时不给出卖出或持有提示',
    reasons: ['已闭合 K 线不足'],
    momentum: [],
    dataTime: null,
    latestPrice: null,
    latestPriceAsOf: null,
    ...unavailableGuidance()
  }
}

function blockedSignal(signal: SwingSignal, label: string, reason: string): SwingSignal {
  return {
    ...signal,
    state: 'unavailable',
    label,
    tone: 'warning',
    outlook: '待核验',
    entryRule: reason,
    exitRule: '恢复为 TRADING 且最新闭合 K 线通过新鲜度门禁后再复核。',
    reasons: [reason, ...signal.reasons],
    ...unavailableGuidance()
  }
}
